import type { ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";
import { useId } from "react";
import { cn } from "~/foundation/utils/cn";

/**
 * The two form controls `@docit/ui` does not carry, in the shape settings pages keep needing:
 * a labelled `<select>` and a labelled `<textarea>`. They match `Input`'s metrics exactly, so a
 * row that mixes all three still lines up, and they own their own label association — the AI
 * administration forms are dense enough that an unlabelled control is a real accessibility bug
 * rather than a theoretical one.
 */

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  hint?: string;
  fullWidth?: boolean;
}

export function Select({ label, hint, fullWidth, className, id, children, ...rest }: SelectProps) {
  const generatedId = useId();
  const selectId = id ?? generatedId;

  return (
    <div className={cn("flex flex-col gap-1.5", fullWidth && "w-full")}>
      {label ? (
        <label htmlFor={selectId} className="text-small font-medium text-text-secondary">
          {label}
        </label>
      ) : null}
      <select
        id={selectId}
        className={cn(
          "h-8 rounded-sm border border-input-border bg-bg px-2 text-body text-text",
          "focus:border-input-border-focus focus:outline-none disabled:opacity-50",
          fullWidth && "w-full",
          className,
        )}
        {...rest}
      >
        {children}
      </select>
      {hint ? <span className="text-small text-text-tertiary">{hint}</span> : null}
    </div>
  );
}

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  hint?: string;
}

export function TextArea({ label, hint, className, id, ...rest }: TextAreaProps) {
  const generatedId = useId();
  const areaId = id ?? generatedId;

  return (
    <div className="flex w-full flex-col gap-1.5">
      {label ? (
        <label htmlFor={areaId} className="text-small font-medium text-text-secondary">
          {label}
        </label>
      ) : null}
      <textarea
        id={areaId}
        className={cn(
          "rounded-sm border border-input-border bg-bg p-2 text-body text-text",
          "focus:border-input-border-focus focus:outline-none disabled:opacity-50",
          className,
        )}
        {...rest}
      />
      {hint ? <span className="text-small text-text-tertiary">{hint}</span> : null}
    </div>
  );
}

/**
 * The tab strip the settings sections share. Deliberately a plain button row rather than a
 * routed tab set: a settings section is one route, and the sub-view is a view state, not an
 * address — the same choice `Runs` already made for its steps/log switch.
 */
export function Tabs<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: Array<{ value: T; label: string; badge?: ReactNode }>;
  onChange: (value: T) => void;
}) {
  return (
    // A long tab row scrolls instead of widening the page past a phone screen (S2-2 browser check, 390 px Hebrew).
    <div className="flex min-w-0 gap-1 overflow-x-auto border-b border-divider">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          aria-current={value === option.value ? "page" : undefined}
          className={cn(
            "flex cursor-pointer items-center gap-1.5 border-b-2 px-2 pb-1.5 text-body",
            value === option.value
              ? "border-accent text-text"
              : "border-transparent text-text-secondary hover:text-text",
          )}
        >
          {option.label}
          {option.badge}
        </button>
      ))}
    </div>
  );
}
